import { Box } from '@mui/material';
import React from 'react';
import { QueuedClientMessage } from '../helpers';
import { LiveChatData } from '../types';
import { getInfoMessage } from '../utils/language';

export type ClientChatComposerInput = {
  value: string;
  height: number | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
};

export type ClientChatComposerQueue = {
  message: QueuedClientMessage | null;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onEdit: () => void;
  onClear: () => void;
};

export type ClientChatComposerFiles = {
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelect: () => void;
};

type ClientChatComposerProps = {
  chatData: LiveChatData | null;
  isLocked: boolean;
  input: ClientChatComposerInput;
  queue: ClientChatComposerQueue;
  files: ClientChatComposerFiles;
  onSend: () => void;
};

const ClientChatComposer: React.FC<ClientChatComposerProps> = ({
  chatData,
  isLocked,
  input,
  queue,
  files,
  onSend
}) => {
  const queuedPreviewText =
    queue.message?.content ||
    (queue.message && queue.message.files.length > 0
      ? queue.message.files.map(file => file.name).join(', ')
      : '');

  return (
    <Box display="flex" flexDirection="row" alignItems="stretch" gap={0.5} className="chatyme-composer-input-row">
      <input
        ref={files.inputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
        onChange={files.onChange}
        style={{ display: 'none' }}
        disabled={isLocked}
      />

      <div className={`chatyme-composer-field${queue.message ? ' chatyme-composer-field-with-queue' : ''}`}>
        {queue.message ? (
          <div className="chatyme-composer-queue">
            <button
              type="button"
              className="chatyme-composer-queue-header"
              onClick={queue.onToggleExpanded}
              aria-expanded={queue.isExpanded}
            >
              <span className="material-icons chatyme-composer-queue-chevron">
                {queue.isExpanded ? 'expand_more' : 'chevron_right'}
              </span>
              <span>{getInfoMessage('messageQueuedCount', chatData)}</span>
            </button>

            {queue.isExpanded ? (
              <div className="chatyme-composer-queue-item">
                <div className="chatyme-composer-queue-body">
                  <span className="chatyme-composer-queue-text">{queuedPreviewText}</span>
                  {queue.message.files.length > 0 && queue.message.content ? (
                    <span className="chatyme-composer-queue-files">
                      {queue.message.files.length} file{queue.message.files.length !== 1 ? 's' : ''}
                    </span>
                  ) : null}
                </div>
                <div className="chatyme-composer-queue-actions">
                  <button
                    type="button"
                    className="chatyme-composer-queue-action"
                    onClick={queue.onEdit}
                    aria-label="Edit queued message"
                    title="Edit"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                  <button
                    type="button"
                    className="chatyme-composer-queue-action"
                    onClick={queue.onClear}
                    aria-label="Delete queued message"
                    title="Delete"
                  >
                    <span className="material-icons">delete</span>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <textarea
          ref={input.textareaRef}
          className="chatyme-chat-input"
          value={input.value}
          onChange={input.onChange}
          onKeyPress={input.onKeyPress}
          placeholder={
            queue.message
              ? getInfoMessage('messageQueuedPlaceholder', chatData)
              : getInfoMessage('typeAMessage', chatData)
          }
          disabled={isLocked}
          rows={1}
          style={input.height ? { height: `${input.height}px` } : undefined}
        />
      </div>

      <button
        className="chatyme-attachment-button"
        onClick={files.onSelect}
        disabled={isLocked}
      >
        <span className="material-icons">attach_file</span>
      </button>
      <button
        className="chatyme-send-button"
        onClick={onSend}
        disabled={isLocked}
      >
        <span className="material-icons">send</span>
      </button>
    </Box>
  );
};

export default ClientChatComposer;
